import { API_BASE_URL } from '~/config';
import { ApiCodeSamples, ApiCodeSamplesConfig } from './';

export function createTextToImageApiCodeSamples(config: ApiCodeSamplesConfig, apiKey = '<API_KEY>'): ApiCodeSamples {
  const url = `${API_BASE_URL}${config.path ?? '/inference/v1/image_generation/'}${config.model.name}`;
  const accept = config.body.accept ?? 'image/jpeg';

  let outputFiletype: string;
  switch (accept) {
    case 'image/jpeg':
      outputFiletype = 'jpg';
      break;
    case 'image/png':
      outputFiletype = 'png';
      break;
    default:
      outputFiletype = 'png';
      break;
  }

  const negativePromptJS = config.body.negative_prompt
    ? `\n      negative_prompt: "${config.body.negative_prompt}"`
    : '';

  const imageName = config.body.prompt.split(' ')[0]
    ? config.body.prompt.split(' ')[0].toLowerCase()
    : '<DEFAULT_IMAGE_NAME>';

  let jsExample = `import fs from "fs";
import fetch from "node-fetch";

(async () => {
    const response = await fetch("${url}", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "Accept": "${accept}",
      "Authorization": "Bearer ${apiKey}"
    },
    body: JSON.stringify({
      cfg_scale: ${config.body.cfg_scale},
      height: ${config.body.height},
      width: ${config.body.width},`;
  if (config.body.sampler) jsExample += `\n      sampler: "${config.body.sampler}"`;
  jsExample += `
      steps: ${config.body.steps},
      seed: ${config.body.seed},
      safety_check: ${String(config.body.safety_check)},
      prompt: "${config.body.prompt}",${negativePromptJS}
    }),
  });

  // To process the response and get the image:
  const buffer = await response.arrayBuffer();

  fs.writeFile('${imageName}.${outputFiletype}', Buffer.from(buffer), () => console.log('finished downloading!'));
})().catch(console.error);`;

  const negativePromptCurl = config.body.negative_prompt
    ? `,\n  "negative_prompt": "${config.body.negative_prompt}"`
    : '';

  let curlExample = `curl --request POST \\
-S --fail-with-body \\
--url ${url} \\
-H 'Content-Type: application/json' \\
-H 'Accept: ${accept}' \\
-H 'Authorization: Bearer ${apiKey}' \\
--data '
{
  "cfg_scale": ${config.body.cfg_scale},
  "height": ${config.body.height},
  "width": ${config.body.width},
  "steps": ${config.body.steps},
  "seed": ${config.body.seed},`;
  if (config.body.sampler) curlExample += `  "sampler": "${config.body.sampler}",`;
  curlExample += `
  "safety_check": ${String(config.body.safety_check)},
  "prompt": "${config.body.prompt}"${negativePromptCurl}
}' -o output.${outputFiletype}`;

  function pythonEscapePrompt(prompt: string): string {
    return prompt.replace(/"/gu, '\\"');
  }

  let pyOutputImageFormat: string;
  switch (accept) {
    case 'image/jpeg':
      pyOutputImageFormat = '\n    output_image_format="JPG",';
      break;
    case 'image/png':
      pyOutputImageFormat = '\n    output_image_format="PNG",';
      break;
    default:
      pyOutputImageFormat = '';
      break;
  }

  const negPromptLine = config.body.negative_prompt
    ? `\n    negative_prompt="${pythonEscapePrompt(config.body.negative_prompt)}",`
    : '';

  const pythonExample = `# pip install 'fireworks-ai'
import fireworks.client
from fireworks.client.image import ImageInference, Answer

# Initialize the ImageInference client
fireworks.client.api_key = "${apiKey}"
inference_client = ImageInference(model="${config.model.id}")

# Generate an image using the text_to_image method
answer : Answer = inference_client.text_to_image(
    prompt="${pythonEscapePrompt(config.body.prompt)}",${negPromptLine}
    cfg_scale=${config.body.cfg_scale},
    height=${config.body.height},
    width=${config.body.width},
    sampler=${config.body.sampler ? `"${config.body.sampler}"` : 'None'},
    steps=${config.body.steps},
    seed=${config.body.seed},
    safety_check=${config.body.safety_check ? 'True' : 'False'},${pyOutputImageFormat}
    # Add additional parameters here
)

if answer.image is None:
  raise RuntimeError(f"No return image, {answer.finish_reason}")
else:
  answer.image.save("output.${outputFiletype}")`;

  return {
    typescript: jsExample,
    python: pythonExample,
    shell: curlExample,
  };
}
